import * as configJSONProd from '../../configProd.json';
import 'whatwg-fetch';
import { UtilityService } from './utility.service';

const uuid = require('uuidv4');
declare const Promise;
window.usercentrics = window.usercentrics || {};

declare global {
    interface Window {
        ccb_cif: {
            pToken: any
        };
    }
}

export class ApiService {
    private configs: any;
    private context: string;
    private graphqlAPI: string;

    private api: any;
    private utilityService: UtilityService;

    constructor(utilityService) {
        this.utilityService = utilityService;
        this.configs = configJSONProd;
        this.context = this.configs.apiContext + '/';
        this.graphqlAPI = this.configs.graphqlAPI;

        this.api = {
            settings: this.configs.apiUrls.settings,
            getConsentInfo: this.configs.apiUrls.getConsentInfo,
            saveConsents: this.configs.apiUrls.saveConsents,
            saveTagLoggerData: this.configs.apiUrls.saveTagLoggerData,
        };
    }

    /**
     *
     * @param language The two character ISO code of the country
     */
    getSettingsFromServer(language: string): any {
        const params = {
            settingsId: window.usercentrics.settingsId,
            version: 'latest',
        };
        let url =
            this.context +
            this.api.settings
                .replace('{settingsId}', params.settingsId)
                .replace('{version}', params.version)
                .replace('{language}', language);
        if (window.usercentrics.paths && window.usercentrics.paths.settings) {
            if (window.usercentrics.paths.settings[language]) {
                url = window.usercentrics.paths.settings[language];
            } else {
                url =
                    window.usercentrics.paths.settings[
                    Object.keys(window.usercentrics.paths.settings)[0]
                    ];
            }
        }

        return fetch(url, {
            method: 'GET',
            mode: 'cors',
        }).then(
            response => {
                this.utilityService.developmentLog('response', response);
                if (response.ok) {
                    return response.json();
                }
                return response.text().then(text => {
                    this.utilityService.developmentLog(
                        'Error from server: ' + text
                    );
                    return null;
                });
            },
            error => {
                return error;
            }
        );
    }

    getConsentInfoFromServer(consent, language) {
        const url =
            this.context +
            this.api.getConsentInfo
                .replace('{consentTemplateId}', consent.templateId)
                .replace('{version}', consent.version)
                .replace('{language}', language);

        return fetch(url, {
            method: 'GET',
            mode: 'cors',
        }).then(response => {
            this.utilityService.developmentLog('response', response);
            if (response.ok) {
                return response.json();
            }
            return response.text();
        });
    }

    saveConsents(paramsToSave) {
        let url = this.graphqlAPI;
        let body = JSON.stringify(paramsToSave);
        let mode: RequestMode = 'cors';
        let allowCredentials = false;
        if (
            window.usercentrics.paths &&
            window.usercentrics.paths.storeConsents
        ) {
            url = window.usercentrics.paths.storeConsents.apiEndpoint;
            allowCredentials = true;
            if (
                window.usercentrics.paths.storeConsents.useRestInsteadOfGraphQL
            ) {
                body = JSON.stringify(paramsToSave.variables.consents);
            }
            if (window.usercentrics.paths.storeConsents.mode) {
                mode = window.usercentrics.paths.storeConsents.mode;
            }
        }

        // PToken against CSRF (Cross-Site-Request-Forgery)
        const headers: any = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'X-Request-ID': uuid()
        };
        let credentials: RequestCredentials = 'omit';

        // Only send credentials when target is not our server
        if (typeof window.ccb_cif !== 'undefined' && typeof window.ccb_cif.pToken !== 'undefined' && allowCredentials) {
            headers.pToken = window.ccb_cif.pToken;
            headers.Cookie = document.cookie;
            credentials = 'include';
        }

        return fetch(url, {
            method: 'POST',
            headers,
            body,
            credentials,
            mode
        }).then(response => {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                return response.json();
            }
            return response.text();
        });
    }

    getConsents(paramsToSave) {
        const url = this.graphqlAPI;
        return fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify(paramsToSave),
            mode: 'cors',
        }).then(response => {
            return response.json();
        });
    }

    /**
     * this function log error in backend.
     * @param severity : this field describes the level of a log message
     * @param message : error message heading , this data will be show up on the top of each log entry
     * @param context : The settingsId for which this request was made
     */
    logMessage(severity, message, context) {
        const url = this.graphqlAPI;
        const paramsToSave: any = {
            operationName: 'addLoggingMessage',
            query: `
            mutation addLoggingMessage(
                    $severity: String!
                    $app: String!
                    $message: String!
                    $stacktrace: String
                    $http: HttpData
                    $context: ContextData
                ) {
                    addLoggingMessage(
                        severity: $severity
                        app: $app
                        message: $message
                        stacktrace: $stacktrace
                        http: $http
                        context: $context
                  )
                }
              `,
            variables: {
                severity: severity,
                app: 'vanilla-app',
                message: message,
                stacktrace: '',
                http: {
                    request: url,
                    response: message
                },
                context: context
            }
        };
        return fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify(paramsToSave),
            mode: 'cors',
        }).then(response => {
            return response.json;
        }, error => {
            console.error(error);
        });

    }

    static getMultiConsentFromServer(id) {
        const param = { request: id };
        const url =
            'https://consent-templates-batch-generation.usercentrics.eu/';

        return fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify(param),
            mode: 'cors',
        })
            .then(response => {
                return Promise.resolve(response.json());
            });
    }

    getMultiConsentsData(hash, lang?) {
        let url = this.context + 'consent-templates/' + hash + '.json';
        if (
            lang &&
            window.usercentrics.paths &&
            window.usercentrics.paths.consentTemplates
        ) {
            if (window.usercentrics.paths.consentTemplates[lang]) {
                url = window.usercentrics.paths.consentTemplates[lang];
            } else {
                url =
                    window.usercentrics.paths.consentTemplates[
                    Object.keys(
                        window.usercentrics.paths.consentTemplates
                    )[0]
                    ];
            }
        }
        return fetch(url, {
            method: 'GET',
            mode: 'cors',
        }).then(
            response => {
                if (response.ok) {
                    return response.json();
                }

                return response.text().then(text => {
                    throw new Error('Response was not okay: ' + text);
                });
            },
            error => error
        );
    }

    saveTagLoggerData(paramsToSave) {
        const url = this.graphqlAPI;
        this.utilityService.developmentLog('fetching data');
        return fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            body: JSON.stringify(paramsToSave),
            mode: 'cors',
        }).then(response2 => {
            this.utilityService.developmentLog('got data', response2);
            return;
        });
    }

    delay(ms: number) {
        const start = +new Date();
        this.utilityService.developmentLog('delay started');
        while (+new Date() - start < ms) {
            // wait for ms millisecond
        }
        this.utilityService.developmentLog('delay ended');
    }

    async getClientLocation() {
        const url = this.graphqlAPI;
        const paramsToSave: any = {
            operationName: 'clientLocation',
            query: `
                query clientLocation {
                    clientLocation {
                        countryName
                        countryCode
                    }
                }
              `,
        };
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                },
                body: JSON.stringify(paramsToSave),
                mode: 'cors',
            });
            const output = await response.json();
            return output.data.clientLocation;
        } catch (e) {
            console.error('api clientLocation failed', e);
            return;
        }
    }
}
